import type Account from '@models/Account';
import { storeToRefs } from 'pinia';
import { computed, Ref } from 'vue';
import { useDeviceStore } from '#src/store/device';
import { useHistoryStore } from '#src/store/history';
import { useOrderStore } from '#src/store/order';
import { useDayjs } from 'composables/dayjs';
import { useAccountStore } from '#src/store/account';

export function useDevicesByAccount(accountKey: Ref<string>) {
  const deviceStore = useDeviceStore();
  const { accountDeviceErrors, accountDeviceRequests, devicesByAccount } = storeToRefs(deviceStore);

  const devices = computed(() => devicesByAccount.value[accountKey.value] || []);
  const deviceCount = computed(() => devices.value.length);
  const error = computed(() => accountDeviceErrors.value[accountKey.value]);
  const isLoading = computed(() => Boolean(accountDeviceRequests.value[accountKey.value]));

  function getAccountDevices(refresh = false) {
    return deviceStore.getAccountDevices(accountKey.value, refresh);
  }

  async function getExtendedDeviceData() {
    await getAccountDevices();
    return deviceStore.getDevices({ serials: devices.value.map(({ serial }) => serial) });
  }

  return {
    deviceCount,
    devices,
    error,
    getAccountDevices,
    getExtendedDeviceData,
    isLoading,
  };
}

export function useDevicesByOrder(orderNumber: Ref<string>) {
  const deviceStore = useDeviceStore();
  const { devicesByOrder, orderDeviceRequests } = storeToRefs(deviceStore);

  const devices = computed(() => devicesByOrder.value[orderNumber.value] || []);
  const isLoading = computed(() => orderDeviceRequests.value[orderNumber.value] || false);

  function disassociate(serial: string) {
    return deviceStore.disassociate(serial);
  }

  function getDevices() {
    return deviceStore.getOrderDevices(orderNumber.value);
  }

  return {
    devices,
    disassociate,
    getDevices,
    isLoading,
  };
}

export function useDeviceBySerial(serial: Ref<string>) {
  const accountStore = useAccountStore();
  const deviceStore = useDeviceStore();
  const orderStore = useOrderStore();
  const historyStore = useHistoryStore();
  const { devices, devicesLoading, isCheckingFleetStatus, ravenFleetError } = storeToRefs(deviceStore);
  const {
    currentDeviceHistory: deviceHistory,
    deviceHistoryError,
    deviceNotes,
    isLoadingHistory,
  } = storeToRefs(historyStore);
  const { accounts } = storeToRefs(accountStore);
  const { dayjs } = useDayjs();

  const device = computed(() => devices.value[serial.value]);
  const account: Ref<Account | undefined> = computed(() => accounts.value[<string>device.value.accountKey]);
  const isLoading = computed(() => Boolean(devicesLoading.value[serial.value]));
  const isNotFound = computed(() => device.value?.error === 'Device not found');
  const isNotInFleet = computed(
    () => !isLoading.value && !isCheckingFleetStatus.value && device.value.isRaven && !device.value.isInFleet
  );
  const lastTransmissionIsOld = computed(() => dayjs().diff(device.value.lastTransmission, 'hour') > 24);
  const lastTransmissionIsValid = computed(
    () => !device.value || isLoading.value || Boolean(device.value.lastTransmission)
  );
  const ravenError = computed(() => {
    if (ravenFleetError.value) {
      return ravenFleetError.value;
    }
    if (account.value?.id !== device.value.ravenFleetId) {
      return `Provisioned to account ID: ${<string>device.value?.ravenFleetId}`;
    }
    return null;
  });

  function associate(orderNumber: string) {
    return orderStore.associate({
      serial: serial.value,
      orderNumber,
    });
  }

  function disassociate() {
    return deviceStore.disassociate(serial.value);
  }

  function getDevice() {
    return deviceStore.getDevice(serial.value);
  }

  function getDeviceHistory() {
    return historyStore.getDeviceHistory(serial.value);
  }

  function init() {
    return deviceStore.initBareDevice(serial.value);
  }

  function provisionRaven(accountId: string) {
    return deviceStore.provisionRaven(serial.value, accountId);
  }

  function setFallbackVin(vin: string | null) {
    return deviceStore.setFallbackVin(serial.value, vin);
  }

  function swap(
    replacementSerial: string,
    accountKey: string,
    { upsTrackingNumber, shipDate }: { upsTrackingNumber: string; shipDate: string }
  ) {
    return deviceStore.swap(serial.value, replacementSerial, accountKey, { upsTrackingNumber, shipDate });
  }

  function terminate(accountKey: string) {
    return deviceStore.terminate(serial.value, accountKey);
  }

  return {
    account,
    associate,
    device,
    deviceHistory,
    deviceHistoryError,
    deviceNotes,
    disassociate,
    getDevice,
    getDeviceHistory,
    init,
    isCheckingFleetStatus,
    isLoading,
    isLoadingHistory,
    isNotFound,
    isNotInFleet,
    lastTransmissionIsOld,
    lastTransmissionIsValid,
    provisionRaven,
    ravenError,
    setFallbackVin,
    swap,
    terminate,
  };
}
